import { makeAutoObservable } from "mobx";
import { IStoreItem } from "@drone-parts.garphild.pro/interfaces";
import dayjs from "dayjs";
import RestApiQuery, { ApiEndpoints } from "../helpers/rest-api-query";
import { notification } from "antd";

export interface ICurrentStoreItem {
  name: string;
  qty: number;
  variants: string;
}

class StoreStorage {
  isDialogOpened = false;
  isDetailDialogOpened = false;

  currentItem: ICurrentStoreItem | null = null;

  items: IStoreItem[] = [];

  newItem: IStoreItem = {
    _id: '',
    itemId: '',
    brandId: '',
    partTypeId: '',
    price: 0,
    deliveryCost: 0,
    status: 0,
    storeId: 0,
    storePlaceId: 0,
    url: '',
    quantity: 0,
    orderUrl: '',
    orderId: '',
    orderDate: new Date(),
  };

  constructor() {
    this.resetNewItem();
    makeAutoObservable(this);
  }

  loadItems = async () => {
    try {
      const response = await RestApiQuery.get<{data: IStoreItem[]}>(ApiEndpoints.Store);

      if (!response) {
        throw new Error("Failed to load items");
      }

      const { data } = response || {};

      this.items = data.map((item: IStoreItem) => {
        return {
          ...item,
          orderDate: dayjs(item.orderDate),
        };
      });
    } catch (error) {
      notification.error({ message: "Failed to load items" });
    }
  }

  openDialog = () => {
    this.isDialogOpened = true;
  }

  closeDialog = () => {
    this.isDialogOpened = false;
  }

  toggleDialog = () => {
    this.isDialogOpened = !this.isDialogOpened;
  }

  setNewItem = (item: IStoreItem) => {
    this.newItem = item;
  }

  saveItem = async () => {
    try {
      const response = await RestApiQuery.post<IStoreItem, { data: IStoreItem[]}>(
        ApiEndpoints.Store,
        this.newItem,
      );
      if (!response) {
        throw new Error("Failed to save item");
      }
      this.items = response.data;
    } catch (error) {
      notification.error({ message: "Failed to save item" });
    }
    this.isDialogOpened = false;
    this.resetNewItem();
  }

  deleteItem = async (id: number) => {
    try {
      const response = await RestApiQuery.del<IStoreItem[]>(`/api/store/${id}`);

      if (!response) {
        throw new Error("Failed to delete item");
      }

      this.items = response;
    } catch (error) {
      notification.error({ message: "Failed to delete item" });
    }
  }

  updateItem = async (id: number, item: IStoreItem) => {
    try {
      const response = await RestApiQuery.put<IStoreItem, IStoreItem[]>(`/api/store/${id}`, item);

      if (!response) {
        throw new Error("Failed to update item");
      }

      this.items = response;
    } catch (error) {
      notification.error({ message: "Failed to update item" });
    }
  }

  setItemStatus = async (id: number, status: number) => {
    try {
      const response = await RestApiQuery.put<Partial<IStoreItem>, IStoreItem[]>(`/api/store/${id}/status`, { status });

      if (!response) {
        throw new Error("Failed to update status");
      }

      this.items = response;
    } catch (error) {
      notification.error({ message: "Failed to update status" });
    }
  }

  setItemPrice = async (id: number, price: number) => {
    try {
      const response = await RestApiQuery.put<Partial<IStoreItem>, IStoreItem[]>(`/api/store/${id}/price`, { price });

      if (!response) {
        throw new Error("Failed to update price");
      }

      this.items = response;
    } catch (error) {
      notification.error({ message: "Failed to update price" });
    }
  }

  setItemDeliveryCost = async (id: number, deliveryCost: number) => {
    try {
      const response = await RestApiQuery.put<Partial<IStoreItem>, IStoreItem[]>(`/api/store/${id}/deliveryCost`, { deliveryCost });

      if (!response) {
        throw new Error("Failed to update delivery cost");
      }

      this.items = response;
    } catch (error) {
      notification.error({ message: "Failed to update delivery cost" });
    }
  }

  resetNewItem() {
    this.newItem = {
      _id: '',
      itemId: '',
      brandId: '',
      partTypeId: '',
      price: 0,
      deliveryCost: 0,
      status: 0,
      storeId: 0,
      storePlaceId: 0,
      url: '',
      quantity: 0,
      orderUrl: '',
      orderId: '',
      orderDate: new Date(),
    }
  }

  setNewItemField = (field: keyof IStoreItem, value: string | string[] | number) => {
    if (this.newItem[field] !== undefined) {
      this.newItem = {
        ...this.newItem,
        [field]: value,
      };
    } else {
      notification.error({ message: 'Unknown field' });
    }
  }

  toggleDetailDialog = (item?: ICurrentStoreItem) => {
    if (item) {
      this.isDetailDialogOpened = true;
      this.currentItem = item;
    } else {
      this.isDetailDialogOpened = false;
      this.currentItem = null;
    }
  }
}

const storeStorage = new StoreStorage();

export default storeStorage;
